import { useEffect, useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, Pager, StatusBadge } from '@/components/kit';
import { FilterSelect, ListBody, SearchBox, Toolbar, useDebounced } from '@/components/inputs';
import DataTable, { RowLink } from '@/components/admin/DataTable';
import ActivityDialog from '@/components/admin/ActivityDialog';
import { ACTIVITY_ACTIONS, ACTIVITY_STYLES } from '@/shared/constants';
import { ACTIVITY_FIELDS } from '@/shared/fields';
import { paged } from '@/shared/data';
import { formatLocalDate, formatRelativeTime, DATE_FORMATS } from '@/utils/date';

const { eq, contains } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/activity', layout: 'owner', access: "profile === 'Admin_c'" };
export const nav = { icon: 'History', label: 'Activity', section: 'Owner', profiles: ['Admin_c'], order: 90 };

const PAGE_SIZE = 25;

export default function AdminActivity() {
  const [action, setAction] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const q = useDebounced(search.trim());

  useEffect(() => {
    setPage(1);
  }, [action, q]);

  const { data, loading, error, run } = useFetch(() => {
    let query = sdk.table('activity_c').select(ACTIVITY_FIELDS);
    if (action !== 'all') query = query.where(eq('action_c', action));
    if (q) query = query.where(contains('target_c', q));
    return query.orderByDesc('CreatedOn').page(page, PAGE_SIZE).fetch().then(paged);
  }, [action, q, page]);

  const list = data?.rows ?? [];
  const filtered = action !== 'all' || !!q;

  function clear() {
    setSearch('');
    setAction('all');
  }

  const columns = [
    {
      label: 'What happened',
      render: (a) => (
        <RowLink to={`/admin/activity/${a.Id}`} sub={a.detail_c}>
          {a.Name}
        </RowLink>
      ),
    },
    { label: 'Area', render: (a) => <StatusBadge value={a.action_c} styles={ACTIVITY_STYLES} /> },
    { label: 'Applies to', className: 'hidden md:table-cell font-mono text-xs', render: (a) => a.target_c },
    {
      label: 'When',
      className: 'hidden md:table-cell text-muted-foreground',
      render: (a) => <span title={formatLocalDate(a.CreatedOn, DATE_FORMATS.WITH_TIME)}>{formatRelativeTime(a.CreatedOn)}</span>,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="Owner"
        title="Activity"
        description="Who did what: orders, payments, tickets, codes, emails and team changes."
        actions={
          <Button variant="outline" onClick={() => setCreateOpen(true)}>
            <ApperIcon name="Plus" size={16} />
            Add entry
          </Button>
        }
      />

      <Toolbar>
        <SearchBox value={search} onChange={setSearch} placeholder="Search by order, email or code…" />
        <FilterSelect value={action} onChange={setAction} values={ACTIVITY_ACTIONS} allLabel="All areas" />
      </Toolbar>

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'History',
          title: filtered ? 'No entries match' : 'Quiet so far',
          description: filtered ? 'Nothing fits these filters.' : 'Entries appear as the team works orders, payments and tickets.',
          action: filtered ? clear : () => setCreateOpen(true),
          actionLabel: filtered ? 'Clear filters' : 'Add entry',
        }}
      >
        <DataTable columns={columns} rows={list} />
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>

      <ActivityDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={run} />
    </div>
  );
}
